import { Component, EventEmitter, Input, Output, ViewChild } from "@angular/core";
import { HttpErrorResponse, HttpStatusCode } from "@angular/common/http";
import { AbstractControl, UntypedFormControl, NG_VALIDATORS, NG_VALUE_ACCESSOR, ValidationErrors, Validators } from "@angular/forms";
import { Message } from "@bbwt/classes";
import { FileDetails } from "@main/file-storage";
import { createOptions as createDocumentTypeOptions, DocumentType } from "@main/file-storage/document-type";
import { DocumentService } from "@project/services/document.service";
import { MessageService } from "primeng/api";
import { FileUpload } from "primeng/fileupload";
import { ControlBase } from "./control-base";
import { DomSanitizer } from "@angular/platform-browser";
import { Dropdown } from "primeng/dropdown";
import { CustomFileUploadFn } from "@mipro/classes/custom-file-upload-fn";

@Component({
    selector: "m-fileUpload",
    templateUrl: "./file-upload.control.html",
    styleUrls: ["./file-upload.control.scss"],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: FileUploadControlComponent,
            multi: true
        },
        {
            provide: NG_VALIDATORS,
            useExisting: FileUploadControlComponent,
            multi: true
        }
    ]
})
export class FileUploadControlComponent extends ControlBase<FileDetails[]> {
    @Input() maxFileSize: number = 10000000;
    @Input() accept: string = "";
    @Input() multiple: boolean = true;
    @Input() fileLimit: number = 1;
    @Input() invalidFileSizeMessageSummary: string = "{0}: Invalid file size, ";
    @Input() invalidFileSizeMessageDetail: string = "maximum upload size is {0}.";
    @Input() invalidFileTypeMessageSummary: string = "{0}: Invalid file type, ";
    @Input() invalidFileLimitMessageDetail: string = "limit is {0} at most.";
    @Input() invalidFileLimitMessageSummary: string = "Maximum number of files exceeded, ";
    @Input() invalidFileTypeMessageDetail: string = "allowed file types: {0}.";
    @Input() style: string;
    @Input() styleClass: string;
    @Input() previewWidth: number = 50;
    @Input() chooseLabel: string;
    @Input() uploadLabel: string;
    @Input() cancelLabel: string;
    @Input() chooseIcon: string = "pi pi-plus";
    @Input() uploadIcon: string = "pi pi-upload";
    @Input() cancelIcon: string = "pi pi-times";
    @Input() mode: string = "advanced";
    @Input() showUploadButton: boolean = false;
    @Input() showCancelButton: boolean = false;
    @Input() showPrivateCheckboxes: boolean = false;

    @Input() enableDuplicateCheck: boolean = false;

    @Input() customFileUploadMethod?: CustomFileUploadFn = null;

    @Input() edit: boolean = false;

    @Input() showDateSelector: boolean = false;
    _selectedDate: Date = new Date();

    @Input()
    set selectedDate(date: Date) {
        this._selectedDate = date;
        this.onDateChange.emit(this._selectedDate);
    }

    get selectedDate(): Date {
        return this._selectedDate;
    }

    @Input() maxDate: Date;
    @Input() minDate: Date;
    @Input() parentDate: Date;
    @Input() parentEntityName: string;

    @Output() readonly onDateChange = new EventEmitter<Date>();
    @Output() readonly noFileError = new EventEmitter<boolean>();
    @Output() readonly checkboxChange = new EventEmitter<boolean>();

    get disabledButtons(): boolean {
        return !this.selectedDocumentType.valid || !this.validDate;
    }
    duplicateWarnings: Map<string, boolean> = new Map();

    @ViewChild("documentType")
    selectedDocumentTypeComponent: Dropdown;

    @Input()
    set documentType(docType: DocumentType) {
        if (docType) {
            this.hasDocumentType = true;
        }

        this.selectedDocumentType.patchValue(docType);
    }

    @ViewChild(FileUpload, { static: true }) pFileUpload: FileUpload;

    selectedDocumentType = new UntypedFormControl(null, Validators.required);
    documentTypeOptions = createDocumentTypeOptions();
    hasDocumentType = false;

    get savedFiles() {
        return this._savedFiles ?? [];
    }

    private _savedFiles: FileDetails[];
    private readonly MessageId = "m-file-upload-click-on-upload";
    private readonly statusesWithReason = [HttpStatusCode.BadRequest, HttpStatusCode.UnsupportedMediaType];
    private readonly errorMessages = {
        fileNotAccepted: "The file was not accepted. Please check the content is not corrupted, the type is valid and the size does not exceeds the maximum."
    };

    constructor(private documentService: DocumentService, private messageService: MessageService, private sanitizer: DomSanitizer) {
        super();
    }

    isImageExtension(file: any): boolean {
        if (file.extension) {
            return [".jpg", ".jpeg", ".png", ".gif", ".svg"].includes(file.extension.toLowerCase());
        }

        if (file.type) {
            return ["image/jpeg", "image/png", "image/gif", "image/jpg", "image/svg+xml"].includes(file.type.toLowerCase());
        }

        return false;
    }

    writeValue(details: FileDetails[]): void {
        this._savedFiles = details;

        if (this.savedFiles) {
            this.pFileUpload.clear();

            this.savedFiles?.forEach(x => {
                let blob = null;
                blob = new Blob([null]);
                blob.id = x.id;
                blob.lastModifiedDate = new Date();
                blob.virtualSize = x.size;
                blob.extension = x.extension;
                blob.name = `${x.fileName}${x.extension}`;
                blob.objectURL = this.sanitizer.bypassSecurityTrustUrl(x.thumbnailUrl ?? x.url);
                blob.private = x.private;
                this.pFileUpload.files.push(blob)

            })

        }
    }

    async onUpload(files) {
        try {
            if (this.edit) {
                this._savedFiles.forEach(x => x.private = files.find(file => file.id == x.id).private ?? x.private);
                this.onChange(this._savedFiles);
                this.pFileUpload.clear();
                return;
            }

            if (!files?.length) {
                return;
            }

            if (this.selectedDocumentType.invalid) {
                this.noFileError.emit(false);
                this.selectedDocumentType.markAsTouched();
                return;
            }

            let details: FileDetails[];
            if (this.customFileUploadMethod) {
                details = await this.customFileUploadMethod(this.selectedDocumentType.value, this.selectedDate, files);
            } else {
                details = await this.documentService.upload(this.selectedDocumentType.value, this.selectedDate, files);
            }

            this.messageService.add(Message.Success("File(s) uploaded successfully"));

            const detailIds = details.map(detail => detail.id);
            const remainingDetails = this.savedFiles.filter(detail => !detailIds.includes(detail.id));
            this._savedFiles = [...remainingDetails, ...details];

            this.onChange(this._savedFiles);
            this.pFileUpload.clear();
            this.duplicateWarnings.clear();
        } catch (error) {
            this.messageService.add(Message.Error(this.uploadErrorMessage(error)));
        }
    }

    private uploadErrorMessage(error: unknown): string {
        if (error instanceof HttpErrorResponse && typeof error.error === "string" && this.statusesWithReason.includes(error.status)) {
            return error.error;
        }

        return this.errorMessages.fileNotAccepted;
    }

    async deleteSavedFile(details: FileDetails) {
        try {
            await this.documentService.delete(details.id);
            this.messageService.add(Message.Success("File deleted successfully"));
            this._savedFiles = this.savedFiles.filter(d => d.id !== details.id);
            this.onChange(this.savedFiles);

            if (this._savedFiles.length ==0) {
                this.noFileError.emit(false);
            } else if (this.pFileUpload.msgs.length == 0) {
                this.noFileError.emit(true);
            }
        } catch {}
    }

    validate(control: AbstractControl): ValidationErrors | null {
        if (control.hasValidator(Validators.required) && (!this.selectedDocumentType.value || this.savedFiles.length === 0)) {
            return {
                required: true
            };
        }

        return null;
    }

    onDocumentTypeChanged(): void {
        if (this.disabledButtons) {
            this.pFileUpload.clear();
            this.duplicateWarnings.clear();
        }
    }

    async filesSelected({ currentFiles }: { currentFiles: File[] }) {
        this.onTouched();

        if (this.enableDuplicateCheck && currentFiles?.length > 0) {
            await this.checkForDuplicates(currentFiles);
        }

        if (this.pFileUpload.msgs.length === 0) {
            this.noFileError.emit(true);

            const hasUploadMessage = this.pFileUpload.msgs.some(m => m.id === this.MessageId);
            if (!hasUploadMessage) {
                this.pFileUpload.msgs.push({
                    severity: "info",
                    closable: true,
                    id: this.MessageId,
                    detail:
                        `Please click the ${this.pFileUpload.uploadButtonLabel} button to save the ` +
                        `file${currentFiles?.length > 1 ? "s" : ""}`
                });
            }
        } else {
            this.noFileError.emit(false);
        }
    }

    private async checkForDuplicates(files: File[]): Promise<void> {
        for (const file of files) {
            const fileKey = this.getFileKey(file);
            const isDuplicate = await this.documentService.checkForDuplicate(file.name, this.getFileExtension(file), file.size);

            this.duplicateWarnings.set(fileKey, isDuplicate);
        }
    }

    private getFileKey(file: File): string {
        return `${file.name}_${file.size}`;
    }

    private getFileExtension(file: File): string {
        const lastDotIndex = file.name.lastIndexOf(".");
        return lastDotIndex !== -1 ? file.name.substring(lastDotIndex) : "";
    }

    fileRemoved() {
        this.onTouched();
        if (this.pFileUpload.files?.length === 1) {
            this.noFileError.emit(false);
            this.clearInfoMessage();
        }

        this.updateDuplicateWarnings();
    }

    filesCleared() {
        this.onTouched();
        this.noFileError.emit(false);
        this.clearInfoMessage();
        this.duplicateWarnings.clear();
    }

    private clearInfoMessage() {
        if (this.pFileUpload.msgs?.length > 0) {
            this.pFileUpload.msgs = this.pFileUpload.msgs.filter(m => m.id !== this.MessageId);
        }
    }

    private updateDuplicateWarnings() {
        const currentFileKeys = this.pFileUpload.files.map(file => this.getFileKey(file));

        const keysToRemove = Array.from(this.duplicateWarnings.keys()).filter(key =>
            !currentFileKeys.includes(key)
        );

        keysToRemove.forEach(key => {
            this.duplicateWarnings.delete(key);
        });
    }

    onDateChanged(date: Date) {
        this.selectedDate = date;
        this.onDateChange.emit(date);
    }

    onCheckboxChange(event) {
        this.checkboxChange.emit(event.checked);
    }

    get validDate() {
        return this.selectedDate instanceof Date;
    }

    hasFileWarning(file: any): boolean {
        const fileKey = this.getFileKey(file);
        return this.duplicateWarnings.get(fileKey) === true;
    }

    clearFileWarning(file: any): void {
        const fileKey = this.getFileKey(file);
        this.duplicateWarnings.set(fileKey, false);
    }
}